/**
 * L6a: Dialoge für unumkehrbare oder begründungspflichtige Schritte einer Rechnung.
 * Markup aus BillingPage.js verschoben; Zustand und Aufrufe liegen in der Seite (InvoiceDraftPage).
 * Freigeben und „Als versendet markieren“ sind keine Dialoge mehr, sondern Inline-Panels der Seite.
 */
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { formatCurrency, formatDate } from './billingUtils';

export function IssueInvoiceDialog({ open, onOpenChange, invoice, findings, isIssuing, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Rechnung ausstellen</DialogTitle>
        </DialogHeader>

        {invoice && (
          <div className="space-y-4">
            {/* Warning */}
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-amber-600 dark:text-amber-400 text-sm">
                  Nach dem Ausstellen wird eine Rechnungsnummer vergeben und die E-Rechnung (ZUGFeRD-PDF mit XML) unveränderlich gespeichert. Änderungen sind danach nur noch durch eine Stornorechnung möglich.
                  Vorher prüft CTIme die Rechnung nach EN 16931 – bei Fehlern wird keine Nummer vergeben.
                </p>
              </div>
            </div>

            {findings.length > 0 && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 space-y-1">
                <p className="text-sm font-medium text-red-700 dark:text-red-300">Prüfung nicht bestanden – nicht ausgestellt:</p>
                <ul className="text-xs text-red-700 dark:text-red-300 list-disc pl-4 space-y-0.5">
                  {findings.map((f, i) => (
                    <li key={i}><span className="font-mono">{f.rule}</span>{f.bt ? ` (${f.bt})` : ''}: {f.message}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">Fehlende Angaben am Kunden oder im Firmenstamm pflegen, Freigabe bleibt bestehen, dann erneut ausstellen.</p>
              </div>
            )}

            {/* Summary */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kunde</span>
                <span className="text-foreground">{invoice.customer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Zeitraum</span>
                <span className="text-foreground">{formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Positionen</span>
                <span className="text-foreground">{invoice.line_items?.length || 0}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span className="text-foreground">Rechnungsbetrag</span>
                <span className="text-cyan-400">{formatCurrency(invoice.gross_total)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isIssuing}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isIssuing ? 'Ausstellen...' : 'Ausstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UnapproveInvoiceDialog({ open, onOpenChange, reason, onReasonChange, isUnapproving, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Freigabe zurückziehen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Die Rechnung wird wieder in den Status "Entwurf" versetzt und kann bearbeitet werden.
          </p>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Grund *</Label>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Bitte geben Sie einen Grund ein..."
              rows={3}
              className="bg-input border-border text-foreground"
            />
            {reason.length > 0 && reason.length < 5 && (
              <p className="text-xs text-red-400">Mindestens 5 Zeichen erforderlich</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isUnapproving || reason.length < 5}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {isUnapproving ? 'Zurückziehen...' : 'Zurückziehen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VoidInvoiceDialog({ open, onOpenChange, reason, onReasonChange, isVoiding, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Rechnung stornieren</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Es wird eine Stornorechnung mit eigener Nummer erzeugt. Die ursprüngliche Rechnung bleibt erhalten. Die enthaltenen Zeiteinträge werden wieder zur Abrechnung freigegeben.
          </p>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Grund der Stornierung *</Label>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Bitte geben Sie den Grund für die Stornierung an..."
              rows={3}
              className="bg-input border-border text-foreground"
            />
            {reason.length > 0 && reason.length < 5 && (
              <p className="text-xs text-red-400">Mindestens 5 Zeichen erforderlich</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isVoiding || reason.length < 5}
            className="bg-red-600 hover:bg-red-700"
          >
            {isVoiding ? 'Storniere...' : 'Stornieren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteInvoiceDialog({ open, onOpenChange, invoice, isDeleting, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Entwurf löschen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Der Entwurf wird gelöscht und die enthaltenen Zeit- und Kilometereinträge werden wieder zur Abrechnung freigegeben.
          </p>

          {invoice && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Entwurf</span>
                <span className="text-foreground">{invoice.draft_reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Kunde</span>
                <span className="text-foreground">{invoice.customer_name}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? 'Löschen...' : 'Löschen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
